import { Trello } from "./types/trello"

// ─── Keys ────────────────────────────────────────────────────────────────────

const FORCE_RENDER_KEY = "render"
const CARD_CUSTOMER_KEY = "qbCustomer"
const CARD_TRACKED_TIME_KEY = "qbTrackedTime"
const BOARD_BILL_TO_KEY = "qbBillTo"
const BOARD_MODE_KEY = "qbMode"

// Every write in here is best-effort. Trello's commands travel over postMessage
// and fail with "Failed to fetch" when the iframe is torn down or the tab wakes
// from sleep with no network — neither of which is worth failing an operation
// over, or reporting.
const bestEffort = async (write: PromiseLike<void>) => {
  try {
    await write
  } catch (e) {
    console.warn("Could not update the QuickBooks customer card badge", e)
  }
}

// ─── Force card back re-render ───────────────────────────────────────────────

// A nudge, not a write anyone depends on: the API holds the link, and the card
// back re-reads it when opened. Unguarded, a failed nudge surfaced as an
// unhandled rejection — "Failed to fetch (Command: set)" — from a tab that had
// simply been left open overnight.
export const forceCardBackRender = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.set("member", "private", FORCE_RENDER_KEY, Math.random().toString()))

// ─── Linked customer, mirrored into Trello shared card data ──────────────────
//
// The link itself lives in the API (CardCustomerLink). This is a denormalized
// copy so `card-badges` can render on the board without one API round trip per
// card — badge callbacks run for every visible card, so they must read local
// data only. Written on link/unlink and backfilled by the card back whenever it
// resolves a name (cards linked before badges existed have no copy yet).

export interface StoredCardCustomer {
  id: string
  name: string
  // The card this copy was written for. Trello duplicates shared card data when
  // a card is copied, but the API-side link is keyed by card id and is not
  // copied — so a mismatch means this copy belongs to the original card and must
  // not be trusted. Absent on copies written before this field existed.
  cardId?: string
}

// The API (CardCustomerLink) is the source of truth and this is only a cache for
// the badge, so a failed write must never fail the operation that triggered it:
// members without card-write access — Trello observers, or anyone on a board
// they don't belong to — will have these rejected, and the card back writes on
// every open.

export const getStoredCardCustomer = async (
  t: Trello.PowerUp.IFrame
): Promise<StoredCardCustomer | undefined> => {
  try {
    return await t.get("card", "shared", CARD_CUSTOMER_KEY)
  } catch {
    return undefined
  }
}

export const setStoredCardCustomer = (
  t: Trello.PowerUp.IFrame,
  customer: StoredCardCustomer
) => bestEffort(t.set("card", "shared", CARD_CUSTOMER_KEY, customer))

export const clearStoredCardCustomer = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.remove("card", "shared", CARD_CUSTOMER_KEY))

// ─── "This card has tracked time", mirrored into Trello shared card data ─────
//
// A flag, not a copy of the entries: the card-back-section capability has to
// decide whether to offer a section at all, and it runs on every card open. A
// local read is the difference between that decision costing nothing and it
// costing an API round trip per card. The entries themselves stay in the API.
//
// Written when the first entry is logged and reconciled by the card back
// whenever it lists entries, so deleting the last one takes the section away.

export interface StoredTrackedTime {
  // Same guard as the customer copy: Trello duplicates shared card data when a
  // card is copied, but the entries are keyed by card id and are not copied.
  cardId: string
  // Total tracked minutes. Here so the card badge can show it — badge callbacks
  // run once per visible card, so they must read local data only. Absent on
  // copies written before the badge existed; those show no time until the card
  // back next reconciles.
  minutes?: number
}

export const getStoredTrackedTime = async (
  t: Trello.PowerUp.IFrame
): Promise<StoredTrackedTime | undefined> => {
  try {
    return await t.get("card", "shared", CARD_TRACKED_TIME_KEY)
  } catch {
    return undefined
  }
}

export const setStoredTrackedTime = (
  t: Trello.PowerUp.IFrame,
  cardId: string,
  minutes: number
) => bestEffort(t.set("card", "shared", CARD_TRACKED_TIME_KEY, { cardId, minutes }))

export const clearStoredTrackedTime = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.remove("card", "shared", CARD_TRACKED_TIME_KEY))

// ─── What this board uses the power-up for ──────────────────────────────────
//
// Two ways of working, and never both at once:
//
//   "customers" — cards map to QuickBooks customers. Linking, the customer
//                 badge, and the customer's own TimeActivity on the card back.
//   "time"      — hours are logged on cards and billed as services, without
//                 curating a customer list first. The customer is named once,
//                 when the invoice is raised.
//
// Board-scoped, because one board can be a client CRM while another is a
// timesheet. Defaults to "customers": that was the whole power-up before time
// tracking existed, so an install that never touches this setting keeps
// working exactly as it did.

export type BoardMode = "customers" | "time"

export const DEFAULT_BOARD_MODE: BoardMode = "customers"

export const getStoredBoardMode = async (t: Trello.PowerUp.IFrame): Promise<BoardMode> => {
  try {
    const mode = await t.get("board", "shared", BOARD_MODE_KEY)
    return mode === "time" || mode === "customers" ? mode : DEFAULT_BOARD_MODE
  } catch {
    // No board context, or Trello unreachable. The default is the safe answer:
    // it shows what every existing install already had.
    return DEFAULT_BOARD_MODE
  }
}

export const setStoredBoardMode = (t: Trello.PowerUp.IFrame, mode: BoardMode) =>
  bestEffort(t.set("board", "shared", BOARD_MODE_KEY, mode))

export const clearStoredBoardMode = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.remove("board", "shared", BOARD_MODE_KEY))

// ─── The board's "bill to" customer ─────────────────────────────────────────
//
// Whose invoice the time report raises. Board-scoped and shared, because it is
// a fact about the board — this board's work is billed to this customer — and
// it should hold for whoever runs the billing, from whichever machine. Small
// enough (one id and a name) that Trello's per-key limit is no concern, unlike
// the per-card service choices, which live in localStorage.

export interface StoredBillTo {
  id: string
  name: string
}

export const getStoredBillTo = async (
  t: Trello.PowerUp.IFrame
): Promise<StoredBillTo | null> => {
  try {
    return (await t.get("board", "shared", BOARD_BILL_TO_KEY)) ?? null
  } catch {
    return null
  }
}

export const setStoredBillTo = (t: Trello.PowerUp.IFrame, billTo: StoredBillTo) =>
  bestEffort(t.set("board", "shared", BOARD_BILL_TO_KEY, billTo))

export const clearStoredBillTo = (t: Trello.PowerUp.IFrame) =>
  bestEffort(t.remove("board", "shared", BOARD_BILL_TO_KEY))

/**
 * Drop everything this power-up mirrored into Trello's own card data, from every
 * card on the board, and the board's own settings with it. Used by `remove-data` so uninstalling doesn't leave
 * QuickBooks customer names behind. Scoping a write by card id (rather than
 * "card") targets a card other than the one in context.
 */
export const clearAllStoredCardData = async (t: Trello.PowerUp.IFrame) => {
  try {
    const cards = await t.cards("id")
    await Promise.all(
      cards.flatMap((card) => [
        bestEffort(t.remove(card.id, "shared", CARD_CUSTOMER_KEY)),
        bestEffort(t.remove(card.id, "shared", CARD_TRACKED_TIME_KEY))
      ])
    )
    await clearStoredBillTo(t)
    await clearStoredBoardMode(t)
  } catch (e) {
    console.warn("Could not clear QuickBooks card data", e)
  }
}
